import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Download, Lock, Printer, RefreshCw, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import RazorpayMock from "@/components/RazorpayMock";
import { openRazorpay } from "@/lib/razorpay";
import AddressFields, { EMPTY_ADDRESS } from "@/components/AddressFields";
import GiftWrapToggle from "@/components/GiftWrapToggle";
import GiftPanel from "@/components/GiftPanel";
import PromoCode, { useCredit } from "@/components/PromoCode";
import { api, BACKEND, errMsg, img, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STAGES = ["Studying the photo…", "Sketching the big head…", "Adding their quirks…", "Inking and colouring…", "Almost there — final touches…"];

function Generating({ c }) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => Math.min(STAGES.length - 1, x + 1)), 7000); return () => clearInterval(t); }, []);
  return (
    <div className="mx-auto max-w-lg text-center" data-testid="caricature-generating">
      <div className="relative mx-auto h-48 w-48">
        <img src={img(c.photo_url)} alt={c.name} className="h-full w-full rounded-full object-cover grayscale" />
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-dashed border-[#3B5BDB]" style={{ animationDuration: "9s" }} />
      </div>
      <p className="overline mt-10">The artist is at work</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight" data-testid="caricature-stage-text">{STAGES[i]}</h1>
      <p className="mt-3 text-sm text-[#8C8498]">Usually 30–60 seconds. You can stay here — we'll refresh automatically.</p>
    </div>
  );
}

export default function CaricatureView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [fmt, setFmt] = useState("digital");
  const [addr, setAddr] = useState(EMPTY_ADDRESS);
  const [rzp, setRzp] = useState(null);
  const [promo, setPromo] = useState(null);
  const [wrap, setWrap] = useState(false);
  const [giftMsg, setGiftMsg] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();
  const reorderId = sp.get("reorder");
  const credit = useCredit(promo ? promo.total : c?.prices?.[fmt] || 0);

  useEffect(() => {
    if (!reorderId) return;
    api.get(`/orders/${reorderId}`).then((r) => {
      const o = r.data;
      setFmt("print");
      if (o.address) setAddr({ ...EMPTY_ADDRESS, ...o.address });
      setWrap(!!o.gift_wrap); setGiftMsg(o.gift_message || "");
      toast.success("Details copied from your previous order — just review and pay.");
    }).catch(() => {});
  }, [reorderId]);

  useEffect(() => {
    let t;
    const load = () => api.get(`/caricatures/${id}`).then((r) => { setC(r.data); if (r.data.status === "generating") t = setTimeout(load, 3000); }).catch((e) => toast.error(errMsg(e)));
    load();
    return () => clearTimeout(t);
  }, [id]);

  if (!c) return <div className="min-h-screen"><Nav /></div>;

  const retry = async () => {
    try { await api.post(`/caricatures/${id}/retry`); setC({ ...c, status: "generating" }); setTimeout(() => nav(0), 50); } catch (e) { toast.error(errMsg(e)); }
  };
  const order = async (e) => {
    e.preventDefault();
    if (fmt === "print" && !/^[1-9][0-9]{5}$/.test(addr.pincode)) return toast.error("Please enter a valid 6-digit PIN code");
    setBusy(true);
    try {
      const { data } = await api.post(`/caricatures/${id}/orders${promo ? `?promo=${encodeURIComponent(promo.code)}` : ""}`, { format: fmt, address: fmt === "print" ? addr : null, gift_wrap: fmt === "print" && wrap, gift_message: fmt === "print" && wrap ? giftMsg.trim() : "", phone: fmt === "digital" ? phone : "" });
      if (data.razorpay.mocked) setRzp({ order: data.order, amount: data.razorpay.amount / 100 });
      else {
        setBusy(true);
        await openRazorpay(data.razorpay, {
          onSuccess: (res) => verify(data.order.id, res),
          onDismiss: () => { setBusy(false); toast("Payment not completed — your order is saved, you can pay again anytime."); },
        });
        return;
      }
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const verify = async (orderId, payment) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/verify`, payment);
      toast.success(fmt === "digital" ? "Paid — your poster is ready to download" : "Paid — your poster is off to the printer");
      nav(`/orders/${orderId}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const pay = async () => {
    const { data: payment } = await api.post(`/orders/${rzp.order.id}/mock-pay`).catch((err) => { toast.error(errMsg(err)); return { data: null }; });
    if (payment) await verify(rzp.order.id, payment);
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        {c.status === "generating" && <Generating c={c} />}

        {c.status === "failed" && (
          <div className="mx-auto max-w-lg text-center" data-testid="caricature-failed">
            <p className="overline">Hmm</p>
            <h1 className="mt-2 font-serif text-3xl tracking-tight">{c.error || "That didn't work."}</h1>
            <p className="mt-3 text-sm text-[#8C8498]">Nothing was charged. A failed attempt doesn't count as your one shot.</p>
            <button data-testid="caricature-retry-btn" onClick={retry} className="btn-primary mt-8"><RefreshCw className="h-4 w-4" />Try again</button>
          </div>
        )}

        {c.status === "ready" && (
          <div className="grid min-w-0 grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0">
              <p className="overline">Caricature · {c.name}</p>
              <h1 className="mt-2 font-serif text-4xl tracking-tight">Meet {c.name}, drawn.</h1>
              <div className="page-shadow relative mt-8 inline-block max-w-full bg-white p-3" data-testid="caricature-result">
                <img src={img(c.preview_url)} alt={`Caricature of ${c.name}`} className="max-h-[70vh] w-auto max-w-full" draggable={false} onContextMenu={(e) => e.preventDefault()} />
                {!c.paid_order_id && <span className="absolute left-5 top-5 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] text-white"><Lock className="h-3 w-3" />Preview · watermark removed after purchase</span>}
              </div>
              <ul className="mt-5 flex flex-wrap gap-2" data-testid="caricature-result-traits">
                {c.traits.map((t) => <li key={t} className="rounded-full border border-[#E8E2D9] bg-white px-3 py-1 text-xs text-[#5E5868]">{t}</li>)}
              </ul>
            </div>

            <aside className="paper h-fit min-w-0 rounded-lg p-6 lg:sticky lg:top-24">
              {c.paid_order_id && !reorderId ? (
                <div data-testid="caricature-paid-panel">
                  <p className="font-serif text-2xl">It's yours.</p>
                  <p className="mt-1 text-sm text-[#5E5868]">Download the full-resolution, watermark-free poster any time.</p>
                  <a data-testid="caricature-download-btn" href={`${BACKEND}/api/caricatures/${id}/download`} className="btn-primary mt-5 w-full"><Download className="h-4 w-4" />Download poster</a>
                  <Link to={`/orders/${c.paid_order_id}`} data-testid="caricature-view-order-link" className="btn-ghost mt-3 w-full">View order</Link>
                  <Link to={`/caricature/${id}?reorder=${c.paid_order_id}`} data-testid="caricature-reorder-print-link" className="btn-ghost mt-3 w-full"><RotateCcw className="h-4 w-4" />Order another A3 print</Link>
                  <GiftPanel c={c} onChange={setC} />
                </div>
              ) : (
                <form onSubmit={order} data-testid="caricature-order-form">
                  <p className="font-serif text-2xl">Love it? Make it real.</p>
                  <div className="mt-5 space-y-3">
                    {[
                      ["digital", Sparkles, "Digital poster", "High-res file, instant download", c.prices.digital],
                      ["print", Printer, "Printed A3 poster", "11.7 × 16.5 in matte, delivered in ~2 weeks", c.prices.print],
                    ].map(([k, I, t, d, p]) => (
                      <button key={k} type="button" data-testid={`caricature-format-${k}`} onClick={() => setFmt(k)} aria-pressed={fmt === k}
                        className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors ${fmt === k ? "border-[#3B5BDB] bg-[#FBF4EF]" : "border-[#E8E2D9] bg-white hover:border-[#d9cfb6]"}`}>
                        <I className={`mt-0.5 h-5 w-5 shrink-0 ${fmt === k ? "text-[#3B5BDB]" : "text-[#8C8498]"}`} strokeWidth={1.6} />
                        <span className="flex-1"><span className="block font-semibold">{t}</span><span className="block text-xs text-[#8C8498]">{d}</span></span>
                        <span className="font-serif text-xl">{inr(p)}</span>
                      </button>
                    ))}
                  </div>
                  {fmt === "print" && <div className="mt-6" data-testid="caricature-address-fields"><AddressFields addr={addr} setAddr={setAddr} prefix="caricature" kind="poster" /><GiftWrapToggle checked={wrap} onChange={setWrap} message={giftMsg} onMessage={setGiftMsg} prefix="caricature" /></div>}
                  {fmt === "digital" && (
                    <div className="mt-6" data-testid="caricature-phone-field">
                      <Label htmlFor="cphone" className="text-sm font-semibold">Mobile number <span className="font-normal text-[#8C8498]">(for your receipt & WhatsApp updates)</span></Label>
                      <Input id="cphone" type="tel" inputMode="tel" autoComplete="tel" data-testid="caricature-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" className="mt-2 h-11 bg-white" />
                    </div>
                  )}
                  <div className="mt-5"><PromoCode key={fmt} amount={c.prices[fmt]} promo={promo} onChange={setPromo} /></div>
                  {credit > 0 && <p className="mt-3 flex justify-between text-sm text-[#3B5BDB]"><span>Referral credit</span><span data-testid="caricature-credit">−{inr(credit)}</span></p>}
                  <button type="submit" disabled={busy} data-testid="caricature-pay-btn" className="btn-primary mt-4 w-full">
                    <Lock className="h-4 w-4" />Pay {inr((promo ? promo.total : c.prices[fmt]) - credit)} with Razorpay
                  </button>
                  <p className="mt-3 text-center text-xs text-[#8C8498]">GST included · {fmt === "print" ? "free shipping" : "download link never expires"}</p>
                </form>
              )}
            </aside>
          </div>
        )}
      </main>
      <RazorpayMock open={!!rzp} amount={rzp?.amount} busy={busy} onPay={pay} onClose={() => setRzp(null)} />
    </div>
  );
}
